import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  ReturnsFlatTeamSettingGroupById,
  type ReturnsFlatTeamSettingGroupId,
} from "./groups";
import { RETURNS_EXCHANGE_ORDER_LINE_ITEM_PRICE_SOURCE_OPTIONS } from "./selection-options";
import {
  EXCHANGE_ORDER_CREATION_ARTIFACT_SETTING_ID,
  RETURN_ADJUSTMENT_ARTIFACT_SETTING_ID,
  RETURNS_AUTOMATION_CODE_FUNCTIONS_GROUP_ID,
} from "./settings-paths";
import {
  automationMoneyRisk,
  customerFacingRisk,
  exchangeSettingsOwner,
  offOn,
  onOff,
} from "./shared";

export interface ReturnsSettingInput extends SettingRegistryMetadataInput {}

export function setting(metadata: ReturnsSettingInput): ReturnsSettingInput {
  return metadata;
}

export function returnsGroup<
  const Group extends ReturnsFlatTeamSettingGroupId,
  const T extends Record<string, ReturnsSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

const ReturnsFlatSettingCatalogRegistry = {
  ...returnsGroup("returns.ui_return_details", {
    "returns/ui/defaultCollapsed": setting({
      label: "Collapse return details by default",
      description:
        "Starts the return details section collapsed when a merchant opens a return.",
      synonyms: [
        "collapse return details",
        "hide return details",
        "return details collapsed",
      ],
      valueSemantics: offOn,
    }),
    "returns/ui/aiReviewDefaultCollapsed": setting({
      label: "Collapse AI review by default",
      description:
        "Starts the AI review section collapsed when a merchant opens a return.",
      synonyms: ["collapse AI review", "hide AI review", "AI review collapsed"],
      valueSemantics: offOn,
    }),
    "returns/ui/lineItemDetailOptions": setting({
      label: "Line item details shown on returns",
      description:
        "Which product details — such as SKU, barcode, or price — appear on each returned line item.",
      synonyms: [
        "line item details",
        "show SKU on return",
        "return line item fields",
        "what shows on returned items",
      ],
    }),
    "returns/ui/productMetafieldDisplays": setting({
      label: "Product metafields shown on returns",
      description:
        "Product metafields surfaced on returned line items, with an optional display name for each.",
      synonyms: [
        "product metafields",
        "show metafields on returns",
        "custom product fields on returns",
      ],
    }),
    "returns/ui/variantMetafieldDisplays": setting({
      label: "Variant metafields shown on returns",
      description:
        "Variant metafields surfaced on returned line items, with an optional display name for each.",
      synonyms: [
        "variant metafields",
        "show variant metafields",
        "custom variant fields on returns",
      ],
    }),
  }),
  ...returnsGroup("returns.ui_item_disposition", {
    "returns/itemDisposition/enabled": setting({
      label: "Enable item disposition",
      description:
        "Lets warehouse staff record a grade and outcome for each returned item while processing.",
      synonyms: [
        "item disposition",
        "enable disposition",
        "grade returned items",
        "disposition returns",
      ],
      valueSemantics: offOn,
    }),
    "returns/itemDisposition/gradeOptions": setting({
      label: "Disposition grade options",
      description:
        "The condition grades staff can assign to a returned item, such as New, Used, or Damaged.",
      synonyms: [
        "grade options",
        "return grades",
        "item condition options",
        "damaged grade",
      ],
    }),
    "returns/itemDisposition/outcomeOptions": setting({
      label: "Disposition outcome options",
      description:
        "What staff can do with a returned item after grading it, such as Restock, Refurbish, or Discard.",
      synonyms: [
        "outcome options",
        "disposition outcomes",
        "restock or discard",
        "what to do with returned item",
      ],
    }),
  }),
  ...returnsGroup("returns.ui_barcodes", {
    "returns/barcode/enabled": setting({
      label: "Show barcodes",
      description:
        "Prints a scannable barcode on return labels so staff can look items up by scanning.",
      synonyms: ["show barcodes", "enable barcodes", "print barcode on return"],
      valueSemantics: offOn,
    }),
    "returns/barcode/width": setting({
      label: "Barcode width",
      description: "Width of the printed barcode label, in inches.",
      synonyms: ["barcode width", "barcode label size", "label width"],
      changeGuidance: {
        summary: "Must match the physical label stock loaded in the printer.",
      },
    }),
    "returns/barcode/height": setting({
      label: "Barcode height",
      description: "Height of the printed barcode label, in inches.",
      synonyms: ["barcode height", "barcode label size", "label height"],
      changeGuidance: {
        summary: "Must match the physical label stock loaded in the printer.",
      },
    }),
    "returns/barcode/topDisplayOption": setting({
      label: "Text above barcode",
      description: "What is printed on the line above the barcode.",
      synonyms: ["text above barcode", "barcode header", "barcode top text"],
    }),
    "returns/barcode/bottomDisplayOption": setting({
      label: "Text below barcode",
      description: "What is printed on the line below the barcode.",
      synonyms: ["text below barcode", "barcode footer", "barcode bottom text"],
    }),
  }),
  ...returnsGroup("returns.ui_gift_receipts", {
    "returns/giftReceipts/enabled": setting({
      label: "Enable gift receipts",
      description:
        "Lets gift recipients start a return without knowing the original order details. Off by default.",
      synonyms: [
        "gift receipts",
        "gift returns",
        "return a gift",
        "recipient return",
      ],
      valueSemantics: offOn,
    }),
    "returns/giftReceipts/missingBuyerInfoDescription": setting({
      label: "Gift receipt request instructions",
      description:
        "The instructions a gift recipient sees in the gift receipt request modal. HTML is supported, including links and formatted text.",
      synonyms: [
        "missing buyer info",
        "gift return instructions",
        "buyer information message",
        "gift receipt request instructions",
      ],
      availability: {
        conditions: [
          {
            type: "settingEquals",
            settingId: "returns/giftReceipts/enabled",
            value: true,
          },
        ],
        unavailableBehavior: "hidden",
        reason: "Only shown when gift receipts are enabled.",
      },
    }),
    "returns/giftReceipts/emailNotifications": setting({
      label: "Email notifications",
      description:
        "Sends an email whenever a shopper submits a gift receipt request. Recipients default to the awaiting processing report emails under Notifications → Returns & claims if none are listed.",
      synonyms: [
        "gift receipt request emails",
        "gift receipt notifications",
        "notify gift receipt request",
      ],
      valueSemantics: offOn,
      availability: {
        conditions: [
          {
            type: "settingEquals",
            settingId: "returns/giftReceipts/enabled",
            value: true,
          },
        ],
        unavailableBehavior: "hidden",
        reason: "Only shown when gift receipts are enabled.",
      },
    }),
    "returns/giftReceipts/notificationEmails": setting({
      label: "Notification emails",
      description:
        "The email addresses that receive a message when a shopper submits a gift receipt request. If left empty, notifications go to the same addresses as Notifications → Returns & claims → Awaiting processing reports.",
      synonyms: [
        "gift receipt notification recipients",
        "gift receipt request email addresses",
      ],
      availability: {
        conditions: [
          {
            type: "settingEquals",
            settingId: "returns/giftReceipts/enabled",
            value: true,
          },
          {
            type: "settingEquals",
            settingId: "returns/giftReceipts/emailNotifications",
            value: true,
          },
        ],
        unavailableBehavior: "hidden",
        reason:
          "Only shown when gift receipts and gift receipt request email notifications are enabled.",
      },
    }),
    "returns/giftReceipts/requireShopperEmailAndGiftInformation": setting({
      label: "Require shopper email and gift information",
      description:
        "When on, shoppers must enter their email and gift details to submit a request. When off, the modal is informational and shows a cancel button instead of submit.",
      synonyms: [
        "require gift receipt shopper email",
        "gift receipt request form",
        "require gift information",
      ],
      valueSemantics: onOff,
      availability: {
        conditions: [
          {
            type: "settingEquals",
            settingId: "returns/giftReceipts/enabled",
            value: true,
          },
        ],
        unavailableBehavior: "hidden",
        reason: "Only shown when gift receipts are enabled.",
      },
    }),
  }),
  ...returnsGroup("returns.ui_external_ra_number", {
    "returns/ui/exposeExternalId": setting({
      label: "Expose external ID",
      description:
        "Shows the return authorization number from a connected external system on the return.",
      synonyms: [
        "external RA number",
        "expose external id",
        "return authorization number",
        "show RA number",
      ],
      valueSemantics: offOn,
    }),
  }),
  ...returnsGroup("returns.ui_return_editing", {
    "returns/returnEditing/allowCustomerCancelReturn": setting({
      label: "Allow customers to cancel returns",
      description:
        "Lets a customer cancel a return themselves after submitting it.",
      synonyms: [
        "customer cancel return",
        "cancel return",
        "let customers cancel",
        "undo return",
      ],
      valueSemantics: offOn,
    }),
    "returns/returnEditing/allowCustomerEditExchangeAddress": setting({
      label: "Allow customers to edit exchange address",
      description:
        "Lets a customer change the shipping address on an exchange after submitting the return.",
      synonyms: [
        "edit exchange address",
        "change shipping address",
        "customer edit address",
      ],
      valueSemantics: offOn,
    }),
  }),
  ...returnsGroup("returns.pos", {
    "pos/defaultPosRestock": setting({
      label: "Restock by default",
      description:
        "Pre-selects the restock option when staff create a return in Shopify POS.",
      synonyms: ["POS restock", "restock by default", "point of sale restock"],
      valueSemantics: onOff,
    }),
    "pos/sendOnlyCompletionEmail": setting({
      label: "Send only completion email for POS returns",
      description:
        "Sends the customer only the return-completed email for POS returns, skipping the creation and label emails they do not need for an in-store return.",
      synonyms: [
        "POS emails",
        "skip label email",
        "in store return emails",
        "completion email only",
      ],
      valueSemantics: onOff,
    }),
    "pos/allowPOSAdjustments": setting({
      label: "Allow fee and bonus adjustments",
      description:
        "Lets store staff add a fee or bonus to returned items while completing a return in Shopify POS.",
      synonyms: [
        "POS adjustments",
        "fee adjustment",
        "bonus adjustment",
        "in-store return adjustment",
      ],
      valueSemantics: onOff,
    }),
    "pos/allowPOSManualDiscounts": setting({
      label: "Allow manual discounts for exchanges",
      description:
        "Lets store staff create a manual discount while completing an exchange in Shopify POS.",
      synonyms: [
        "POS manual discount",
        "exchange discount",
        "in-store exchange discount",
        "match return value",
      ],
      valueSemantics: onOff,
    }),
  }),
  ...returnsGroup("returns.pos_integration_locations", {
    "pos/externalLocationMappings": setting({
      label: "Integration location mappings",
      description:
        "Maps each Shopify POS store location to the location an ERP or 3PL integration should use, so records created for an in-store return land at the store the customer returned to instead of the integration's default returns location.",
      synonyms: [
        "POS location mapping",
        "in-store location mapping",
        "NetSuite location for POS returns",
        "store location mapping",
      ],
    }),
  }),
  ...returnsGroup("returns.processing", {
    "returns/processing/shopAgainAction": setting({
      label: "Store credit type",
      description:
        "Where store credit is issued when a return is processed — a Shopify gift card, a discount code, a customer account balance, or a connected loyalty provider. Always set; defaults to a Shopify gift card.",
      synonyms: [
        "store credit type",
        "gift card",
        "discount code",
        "how store credit is issued",
        "credit provider",
        "rise credit",
      ],
      valueSemantics: {
        defaultValue: "gift_card",
        summary:
          "Always has a value; defaults to issuing store credit as a Shopify gift card.",
      },
      risk: {
        level: "high",
        reason: "Changes how every future store credit payout is delivered.",
      },
      changeGuidance: {
        summary:
          "Confirm the destination provider is connected before switching — an unconnected provider silently fails to issue credit.",
      },
    }),
    "returns/processing/riseTriggerUrl": setting({
      label: "Rise trigger URL",
      description:
        "The Rise custom trigger URL Redo calls to issue store credit.",
      synonyms: ["rise trigger", "rise URL", "rise credit webhook"],
      dependencies: [
        {
          key: "returns/processing/shopAgainAction",
          relationship: "controlledBy",
          description: "Only used when store credit is issued through Rise.",
        },
      ],
    }),
    "returns/processing/giftCardOrderProductId": setting({
      label: "Gift card product",
      description:
        "The Shopify gift card product Redo orders when issuing store credit as a gift card order.",
      synonyms: [
        "gift card product",
        "gift card order product",
        "which gift card",
      ],
      dependencies: [
        {
          key: "returns/processing/shopAgainAction",
          relationship: "controlledBy",
          description:
            "Only used when store credit is issued as a gift card order.",
        },
      ],
    }),
    "returns/processing/hideGiftCardBranding": setting({
      label: "Hide Redo gift card branding",
      description:
        "Replaces the REDO prefix on issued gift card codes with random characters.",
      synonyms: [
        "hide redo branding",
        "gift card branding",
        "REDO prefix",
        "white label gift card",
      ],
      valueSemantics: offOn,
    }),
    "returns/processing/allowRestockBeforeProcess": setting({
      label: "Decouple restock from return processing",
      description:
        "Lets staff restock returned items before the return is processed, instead of only as part of processing.",
      synonyms: [
        "decouple restock",
        "restock before processing",
        "restock separately",
      ],
      valueSemantics: offOn,
    }),
    "returns/processing/checkForTryNowTags": setting({
      label: "Check for TryNow product tags",
      description:
        "Looks for TryNow product tags so Redo skips creating refunds on TryNow items.",
      synonyms: ["TryNow", "try now tags", "trynow product tags"],
      valueSemantics: offOn,
    }),
  }),
  ...returnsGroup("returns.advanced_processing", {
    "returns/advancedProcessing/compensationEnabled": setting({
      label: "Use advanced compensation processing",
      description:
        "Lets a published Compensation flow decide when refunds, store credit, and exchanges are processed. Standard processing remains active until the flow is published.",
      synonyms: [
        "advanced compensation processing",
        "enable compensation flow",
        "compensation processing flow",
      ],
      valueSemantics: offOn,
    }),
    "returns/advancedProcessing/restockingEnabled": setting({
      label: "Use advanced restocking flow",
      description:
        "Lets a published Restocking flow control inventory separately from compensation. Until it is published, compensation follows the existing inventory automation setting.",
      synonyms: [
        "advanced restocking flow",
        "enable restocking flow",
        "separate restocking flow",
      ],
      valueSemantics: offOn,
    }),
  }),
  ...returnsGroup("returns.exchanges_order_notes", {
    "returns/exchanges/orderNotes/disabled": setting({
      label: "Disable exchange order notes",
      description: "Stops Redo from writing any note onto exchange orders.",
      synonyms: [
        "disable exchange notes",
        "turn off exchange notes",
        "no order note",
      ],
      valueSemantics: offOn,
    }),
    "returns/exchanges/orderNotes/includeDate": setting({
      label: "Include current date in note",
      description: "Adds the current date to the exchange order note.",
      synonyms: ["date in note", "include date"],
      valueSemantics: offOn,
    }),
    "returns/exchanges/orderNotes/includeExistingNote": setting({
      label: "Include existing order note",
      description:
        "Keeps the original order's note and appends the exchange note to it. On by default.",
      synonyms: ["keep existing note", "append to note"],
      valueSemantics: onOff,
    }),
    "returns/exchanges/orderNotes/addNoteToOriginalOrders": setting({
      label: "Add note to original orders",
      description:
        "Also writes a note onto the original order pointing at the exchange.",
      synonyms: ["note on original order", "original order note"],
      valueSemantics: offOn,
    }),
  }),
  ...returnsGroup("returns.exchanges_shipping_name", {
    "returns/exchanges/exchangeShippingName": setting({
      label: "Outgoing shipping line name",
      description: "The shipping method name customers see on exchange orders.",
      synonyms: [
        "exchange shipping name",
        "shipping line name",
        "shipping method name",
      ],
    }),
  }),
  ...returnsGroup("returns.exchanges_fulfillment", {
    "returns/exchanges/fulfillment/excessExchangeValue": setting({
      label: "Leftover exchange value policy",
      description:
        "How Redo compensates a customer when the exchange items cost less than what they returned, or stock is unavailable. Defaults to store credit, so leftover value is never simply dropped.",
      synonyms: [
        "leftover exchange value",
        "excess value",
        "cheaper exchange",
        "out of stock policy",
      ],
      valueSemantics: {
        defaultValue: "store_credit",
        unsetLabel: "Store credit",
        summary:
          "When unset, leftover value is issued as store credit — there is no state in which the difference is simply dropped.",
      },
      risk: {
        level: "high",
        reason: "Decides whether leftover value is paid back to the customer.",
      },
    }),
    "returns/exchanges/fulfillment/outOfStockAdvanced": setting({
      label: "Advanced out of stock behavior",
      description:
        "What happens when part of an exchange order is out of stock — fulfill what is available or hold the whole order. Always has a value; when nothing has been chosen the exchange is cancelled.",
      synonyms: [
        "out of stock",
        "partial fulfillment",
        "hold exchange",
        "advanced out of stock",
      ],
      valueSemantics: {
        defaultValue: "cancel",
        summary:
          "Defaults to cancelling the exchange when an item is out of stock.",
      },
    }),
  }),
  ...returnsGroup("returns.exchanges_fulfillment_regions", {
    "returns/exchanges/fulfillmentRegions/enabled": setting({
      label: "Enable fulfillment region filtering",
      description:
        "Routes exchange orders to specific fulfillment locations based on the destination country.",
      synonyms: [
        "fulfillment regions",
        "region filtering",
        "route by country",
        "location by country",
      ],
      valueSemantics: offOn,
    }),
    "returns/exchanges/fulfillmentRegions/regionMappings": setting({
      label: "Country to location mappings",
      description:
        "Which fulfillment locations serve exchange orders for each country.",
      synonyms: ["country mapping", "region mapping", "locations per country"],
      dependencies: [
        {
          key: "returns/exchanges/fulfillmentRegions/enabled",
          relationship: "requires",
          description:
            "Only used when fulfillment region filtering is enabled.",
        },
      ],
    }),
    "returns/exchanges/fulfillmentRegions/defaultLocationIds": setting({
      label: "Default fulfillment locations",
      description:
        "Locations used for exchange orders when no country mapping matches.",
      synonyms: ["default location", "fallback location"],
    }),
  }),
  ...returnsGroup("returns.exchanges_orders", {
    "returns/exchanges/orders/createTrigger": setting({
      label: "Exchange order trigger",
      description:
        "The point in the return lifecycle at which the exchange order is created. Always in effect; defaults to creating the order once all products are processed.",
      synonyms: [
        "exchange order trigger",
        "when to create exchange",
        "exchange creation",
      ],
      valueSemantics: {
        defaultValue: "all_products_processed",
        unsetLabel: "After all products are processed",
        summary:
          "Not stored directly: derived from the on-hold-orders and non-instant provision settings, so no stored value still means an active trigger.",
      },
      changeGuidance: {
        summary:
          "Creating exchange orders before the return is delivered ships product before it comes back.",
      },
    }),
    "returns/exchanges/orders/labelWaitForInvoice": setting({
      label: "Create label after invoice",
      description:
        "Waits for the exchange order invoice to be paid before buying the outbound label.",
      synonyms: ["wait for invoice", "label after invoice"],
      valueSemantics: offOn,
    }),
    "returns/exchanges/orders/shopifyExchangeOrderStrategy": setting({
      label: "Shopify exchange method",
      description:
        "How the exchange is represented in Shopify — as a new order or an edit to the existing one.",
      synonyms: [
        "shopify exchange method",
        "exchange strategy",
        "new order or order edit",
      ],
      dependencies: [
        {
          key: "discountDistributionMethod",
          relationship: "controls",
          description:
            "Shopify Exchange API is unavailable while order-wide discount distribution is selected, and order-wide distribution is unavailable while Exchange API is selected. Selecting Exchange API resets distribution to Do not distribute.",
        },
      ],
    }),
    "returns/exchanges/orders/addAdditionalExchangeTags": setting({
      label: "Add additional exchange tags",
      description: "Adds extra tags to exchange orders for reporting.",
      synonyms: ["exchange tags", "additional tags"],
      valueSemantics: offOn,
    }),
    "returns/exchanges/orders/shouldRemoveHoldTagWhenRelease": setting({
      label: "Remove hold tag when released",
      description:
        "Removes the hold tag from an exchange order once it is released for fulfillment.",
      synonyms: ["hold tag", "remove hold tag", "release exchange"],
      valueSemantics: offOn,
    }),
    "returns/exchanges/orders/separateQuantityForExchangeProducts": setting({
      label: "Separate exchange items by quantity",
      description:
        "Splits exchange line items so each unit is its own line rather than one line with a quantity.",
      synonyms: ["separate quantity", "split line items"],
      valueSemantics: offOn,
    }),
    "returns/exchanges/orders/applyFeesAsLineItem": setting({
      label: "Apply fees as line item",
      description:
        "Adds return fees to the exchange order as a line item instead of a discount adjustment.",
      synonyms: ["fees as line item", "fee line item"],
      valueSemantics: offOn,
    }),
    "returns/exchanges/orders/applyFeesAsLineItemName": setting({
      label: "Fee line item name",
      description:
        'The product name shown for the fee line item Redo adds to an exchange order. Defaults to "Return Fee" when blank.',
      synonyms: [
        "fee name",
        "fee line item name",
        "what the fee is called",
        "fee label",
        "name of the fee",
      ],
      valueSemantics: {
        defaultValue: "Return Fee",
        unsetLabel: "Return Fee",
        summary: 'Defaults to "Return Fee" when left blank.',
      },
      dependencies: [
        {
          key: "returns/exchanges/orders/applyFeesAsLineItem",
          relationship: "requires",
          description: "Only used when fees are applied as a line item.",
        },
      ],
    }),
    "returns/exchanges/orders/nonZeroValueExchangeStrategy": setting({
      label: "Even exchange new order value",
      description:
        "Whether an even exchange creates a zero-value order or one at full product price. Always set; defaults to a zero-value order.",
      synonyms: [
        "even exchange value",
        "zero value order",
        "exchange order price",
      ],
      valueSemantics: {
        defaultValue: "none",
        summary:
          "Always has a value; defaults to none, so even exchanges create a zero-value order.",
      },
    }),
    "returns/exchanges/orderName/reuseOrderName": setting({
      label: "Reuse order number",
      description:
        "Names the exchange order after the original order rather than assigning a new number.",
      synonyms: [
        "reuse order number",
        "same order number",
        "exchange order name",
      ],
      valueSemantics: offOn,
    }),
    "returns/exchanges/orderName/shouldStripHash": setting({
      label: "Strip hash from order name",
      description:
        "Removes the leading # when reusing the original order number.",
      synonyms: ["strip hash", "remove hash", "order number hash"],
      valueSemantics: offOn,
      dependencies: [
        {
          key: "returns/exchanges/orderName/reuseOrderName",
          relationship: "requires",
          description: "Only used when the order number is reused.",
        },
      ],
    }),
    "returns/exchanges/orderName/prefix": setting({
      label: "Exchange order name prefix",
      description: "Text placed before the exchange order number.",
      synonyms: ["order prefix", "exchange prefix"],
    }),
    "returns/exchanges/orderName/suffix": setting({
      label: "Exchange order name suffix",
      description: "Text placed after the exchange order number.",
      synonyms: ["order suffix", "exchange suffix"],
    }),
    "returns/exchanges/orderName/returnType": setting({
      label: "Order name request type",
      description:
        "Which request type — exchange, claim, or warranty — the prefix and suffix apply to.",
      synonyms: ["order name type", "prefix applies to"],
    }),
    "returns/exchanges/orderName/duplicateOrderNumberFormat": setting({
      label: "Duplicate order number format",
      description:
        'The format used when a reused order number would collide with an existing order. Defaults to " ({n})".',
      synonyms: [
        "duplicate order number",
        "order number collision",
        "duplicate format",
      ],
      valueSemantics: {
        defaultValue: " ({n})",
        unsetLabel: " ({n})",
        summary:
          'Defaults to " ({n})" — the collision counter appended to the reused order number.',
      },
    }),
  }),

  ...returnsGroup("returns.exchanges_order_pricing", {
    "returns/exchanges/exchangeFinancialStrategy": setting({
      label: "Exchange order pricing strategy",
      description:
        "How Redo prices the exchange order it creates — set item prices to the upsell amount, mark the order as paid using the selected item prices, or apply an order discount down to the upsell amount. Draft orders use a zero total for the upsell and order-discount strategies.",
      synonyms: [
        "exchange pricing strategy",
        "exchange financial strategy",
        "upsell value",
        "full price exchange",
        "how exchange orders are priced",
      ],
      valueSemantics: {
        selectionMode: "single",
        options: [
          { value: "upsell_value", label: "Use only the upsell amount" },
          { value: "full_price", label: "Mark the order as paid" },
          {
            value: "full_price_discount_to_upsell_value",
            label: "Apply an order discount and mark the order as paid",
          },
        ],
      },
    }),
    "returns/exchanges/exchangeOrderLineItemPriceSource": setting({
      label: "Exchange item prices",
      description:
        "Choose full retail prices before discounts or discounted prices for exchange items. Applies to Mark the order as paid and Apply an order discount strategies, not orders priced using only the upsell amount.",
      synonyms: [
        "exchange item prices",
        "exchange item price source",
        "original exchange item prices",
        "discounted exchange item prices",
      ],
      valueSemantics: {
        selectionMode: "single",
        defaultValue: "discountedPrice",
        unsetLabel: "Use discounted price",
        options: RETURNS_EXCHANGE_ORDER_LINE_ITEM_PRICE_SOURCE_OPTIONS,
      },
      availability: {
        conditions: [
          {
            type: "evaluator",
            evaluator: "integration.connected",
            integration: "shopify",
          },
          {
            type: "settingEquals",
            settingId: "returns/exchanges/orders/shopifyExchangeOrderStrategy",
            value: "order",
          },
        ],
        unavailableBehavior: "hidden",
        reason: "Only shown for Redo-created Shopify exchange orders.",
      },
      dependencies: [
        {
          key: "returns/exchanges/exchangeFinancialStrategy",
          relationship: "requires",
          description:
            "Requires the full-price or full-price-discounted-to-upsell strategy. The pricing card also requires the new exchange financial strategy rollout or an already migrated team; these OR conditions need a new availability evaluator.",
        },
      ],
    }),
    "returns/exchanges/addTaxToExchangeOrders": setting({
      label: "Include tax on exchange orders",
      description:
        "Applies taxes when calculating the exchange order total, rather than pricing the exchange tax-exclusive.",
      synonyms: [
        "add tax to exchange",
        "include tax on exchange",
        "exchange order tax",
        "tax on exchange orders",
      ],
      valueSemantics: offOn,
    }),
    "returns/exchanges/applyExchangeOrderPaymentGateways/enabled": setting({
      label: "Apply exchange order payment gateways",
      description:
        "Assigns separate Shopify payment gateway names to the upsell and return-value portions of exchange orders.",
      synonyms: [
        "exchange gateways",
        "exchange payment gateway",
        "split exchange payments",
      ],
      valueSemantics: offOn,
    }),
    "returns/exchanges/applyExchangeOrderPaymentGateways/upsellGateway":
      setting({
        label: "Upsell gateway",
        description:
          "The Shopify payment gateway name assigned to the amount the customer owes on an exchange.",
        synonyms: ["exchange upsell gateway", "upsell payment method"],
        dependencies: [
          {
            key: "returns/exchanges/applyExchangeOrderPaymentGateways/enabled",
            relationship: "requires",
            description:
              "Only used when exchange order payment gateways are enabled.",
          },
        ],
      }),
    "returns/exchanges/applyExchangeOrderPaymentGateways/returnValueGateway":
      setting({
        label: "Return value gateway",
        description:
          "The Shopify payment gateway name assigned to the returned-item value applied to an exchange order.",
        synonyms: ["exchange return value gateway", "return payment method"],
        dependencies: [
          {
            key: "returns/exchanges/applyExchangeOrderPaymentGateways/enabled",
            relationship: "requires",
            description:
              "Only used when exchange order payment gateways are enabled.",
          },
        ],
      }),
  }),

  ...returnsGroup("returns.permissions", {
    "returns/permissions/allowRefund": setting({
      label: "Allow refund",
      description:
        "Lets base-role users choose a refund when creating a return for a customer. Stored as an allow-list: until it is saved once, this page shows it enabled but POS denies base-role users the method.",
      synonyms: ["allow refund", "who can refund", "refund permission"],
      valueSemantics: { falseLabel: "Off", trueLabel: "On" },
    }),
    "returns/permissions/allowStoreCredit": setting({
      label: "Allow store credit",
      description:
        "Lets base-role users choose store credit when creating a return for a customer. Stored as an allow-list: until it is saved once, this page shows it enabled but POS denies base-role users the method.",
      synonyms: ["allow store credit", "store credit permission"],
      valueSemantics: { falseLabel: "Off", trueLabel: "On" },
    }),
    "returns/permissions/allowExchange": setting({
      label: "Allow exchange",
      description:
        "Lets base-role users choose an exchange when creating a return for a customer. Stored as an allow-list: until it is saved once, this page shows it enabled but POS denies base-role users the method.",
      synonyms: ["allow exchange", "exchange permission"],
      valueSemantics: { falseLabel: "Off", trueLabel: "On" },
    }),
    "returns/permissions/allowEditFeesAndBonuses": setting({
      label: "Allow editing fees and bonuses",
      description:
        "Lets base-role users change return fees and exchange bonuses on the review page. Granted by default.",
      synonyms: [
        "edit fees",
        "edit bonuses",
        "fee permission",
        "override fees",
      ],
      valueSemantics: onOff,
    }),
    "returns/permissions/allowEditGreenReturns": setting({
      label: "Allow editing green returns",
      description:
        "Lets base-role users turn items into green (keep-the-item) returns on the review page. Granted by default.",
      synonyms: [
        "edit green returns",
        "green return permission",
        "keep the item permission",
      ],
      valueSemantics: onOff,
    }),
    "returns/permissions/allowEditReturn": setting({
      label: "Allow editing returns",
      description:
        "Lets base-role users edit return details after creation — compensation methods, monetary values, and exchange orders. Granted by default.",
      synonyms: [
        "edit return",
        "edit return permission",
        "change return amounts",
      ],
      valueSemantics: onOff,
    }),
  }),

  ...returnsGroup("returns.shipping_label_combination", {
    "returns/shipping/skuGroupingEnabled": setting({
      label: "SKU grouping",
      description:
        "Combines returned items that share a SKU group onto a single return label.",
      synonyms: ["sku grouping", "combine labels", "group items on one label"],
      valueSemantics: offOn,
    }),
    "returns/shipping/skuGroupingCsvFileUrl": setting({
      label: "SKU grouping CSV",
      description:
        "The uploaded CSV that defines which SKUs belong to the same group for combining return labels.",
      synonyms: [
        "sku grouping csv",
        "sku group upload",
        "combine labels csv",
        "sku grouping file",
      ],
      dependencies: [
        {
          key: "returns/shipping/skuGroupingEnabled",
          relationship: "controlledBy",
          description: "Only used when SKU grouping is enabled.",
        },
      ],
    }),
    "returns/shipping/onlyCombineLabelsFromSameFulfillment": setting({
      label: "Group items from original fulfillment",
      description:
        "Only combines items onto one label when they shipped in the same original fulfillment.",
      synonyms: ["same fulfillment", "combine from same shipment"],
      valueSemantics: offOn,
    }),
    "returns/shipping/verifyAddressForLabel": setting({
      label: "Verify merchant address for labels",
      description:
        "Validates the merchant return address with the carrier before buying a label.",
      synonyms: [
        "verify address",
        "address validation",
        "check return address",
      ],
      valueSemantics: offOn,
    }),
  }),

  ...returnsGroup("returns.shipping_dropoff_providers", {
    "returns/shipping/dropoffs": setting({
      label: "Dropoff providers",
      description:
        "Which third-party dropoff networks customers can use to return items without printing a label, and the search radius for each.",
      synonyms: [
        "dropoff providers",
        "happy returns",
        "label free dropoff",
        "return bar",
      ],
    }),
  }),

  ...returnsGroup("returns.shipping_return_locations", {
    "returns/shipping/returnInStoreEnabled": setting({
      label: "Enable in-store returns",
      description:
        "Lets customers return items at a physical store location instead of shipping them back.",
      synonyms: [
        "return in store",
        "in store returns",
        "bring it to a store",
        "store returns",
      ],
      valueSemantics: offOn,
    }),
    "returns/shipping/returnInStoreRadiusMiles": setting({
      label: "In-store return radius (miles)",
      description:
        "How far from the customer Redo will look for a store that accepts returns. Defaults to 100 miles when left blank.",
      synonyms: ["return radius", "store distance", "how far to search"],
      valueSemantics: {
        defaultValue: 100,
        unit: "miles",
        unsetLabel: "100 miles",
        summary: "Defaults to 100 miles; the minimum accepted value is 1.",
      },
      dependencies: [
        {
          key: "returns/shipping/returnInStoreEnabled",
          relationship: "requires",
          description: "Only used when in-store returns are enabled.",
        },
      ],
    }),
    "returns/shipping/inStoreLocations": setting({
      label: "Store locations accepting returns",
      description:
        "Which Shopify locations are offered to customers as in-store return destinations.",
      synonyms: ["store locations", "which stores accept returns"],
    }),
    "returns/shipping/customInStoreLocations": setting({
      label: "Custom in-store locations",
      description:
        "Additional return locations that do not exist as Shopify locations.",
      synonyms: ["custom locations", "manual store locations"],
    }),
  }),

  ...returnsGroup("returns.shipping_options", {
    "returns/shipping/packingSlipEnabled": setting({
      label: "Include packing slip",
      description:
        "Includes a packing slip with the return label so the warehouse can identify the return.",
      synonyms: ["packing slip", "include packing slip", "return slip"],
      valueSemantics: offOn,
    }),
    "returns/shipping/includeExchangeItemsOnPackingSlip": setting({
      label: "Include exchange items on packing slip",
      description:
        "Lists the outgoing exchange items on the return packing slip.",
      synonyms: ["exchange items on slip", "packing slip exchange"],
      valueSemantics: offOn,
    }),
    "returns/shipping/manualLabelPackingSlipEnabled": setting({
      label: "Packing slip for manual labels",
      description:
        "Includes a packing slip when the customer supplies their own return label. On by default.",
      synonyms: ["manual label packing slip", "own label slip"],
      valueSemantics: onOff,
    }),
    "returns/shipping/inStoreDropOffPackingSlipEnabled": setting({
      label: "Packing slip for in-store dropoff",
      description: "Includes a packing slip for in-store return dropoffs.",
      synonyms: ["in store packing slip", "store dropoff slip"],
      valueSemantics: offOn,
    }),
    "returns/shipping/deductLabelFromCredit": setting({
      label: "Deduct label cost from store credit",
      description:
        "Subtracts the return label cost from the store credit the customer receives.",
      synonyms: [
        "deduct label",
        "charge customer for label",
        "label cost from credit",
      ],
      risk: { level: "high", reason: "Changes what the customer is paid out." },
      valueSemantics: offOn,
    }),
    "returns/shipping/deductLabelFromRefund": setting({
      label: "Deduct label cost from refund",
      description:
        "Subtracts the return label cost from the customer's refund.",
      synonyms: ["deduct label from refund", "label cost from refund"],
      risk: { level: "high", reason: "Changes what the customer is paid out." },
      valueSemantics: offOn,
    }),
    "returns/shipping/deductLabelFromExchange": setting({
      label: "Deduct label cost from exchange",
      description:
        "Subtracts the return label cost from the value applied to an exchange.",
      synonyms: ["deduct label from exchange", "exchange label cost"],
      valueSemantics: offOn,
    }),
    "returns/shipping/customerLabelUpchargeFee": setting({
      label: "Customer label fee",
      description:
        "Extra amount charged to the customer on top of the label cost. This is a direct fee, distinct from deducting the label cost out of a refund, store credit, or exchange value.",
      synonyms: [
        "label fee",
        "upcharge",
        "return shipping fee",
        "charge customer for label",
        "charge for return shipping",
      ],
      risk: { level: "high", reason: "Directly charges the customer." },
    }),
    "returns/shipping/dontChargeCustomerLabelUpchargeWhenOnlyOption": setting({
      label: "Waive label fee when it is the only option",
      description:
        "Skips the customer label fee when no other return method is available to them.",
      synonyms: ["waive label fee", "only option fee"],
      valueSemantics: offOn,
    }),
    "returns/shipping/excludeCustomerLabelFeeForExchanges": setting({
      label: "Waive label fee for exchanges",
      description:
        "Skips the customer label fee when the return is an exchange.",
      synonyms: ["no fee on exchange", "exchange label fee waived"],
      valueSemantics: offOn,
    }),
    "returns/shipping/includeMultipleCarrierOptions": setting({
      label: "Shopper return carrier options",
      description:
        "Lets customers choose among eligible return carriers in the returns portal. Redo records the carrier quotes for every return either way; turning this off keeps returns on the cheapest covered carrier without showing the choice.",
      synonyms: [
        "shopper return carrier options",
        "include multiple carrier options",
        "return carrier options",
        "enable return carrier options",
        "choose return carrier",
        "shopper carrier selection",
      ],
      visibility: "admin",
      risk: customerFacingRisk,
      valueSemantics: offOn,
    }),
    "returns/shipping/merchantMaxLabelPrice": setting({
      label: "Maximum label price",
      description:
        "Caps the rate Redo will buy for a single return label when the merchant is the label payer. Customer-paid and Redo-paid labels use their own separate caps, so this has no effect on them.",
      synonyms: ["max label price", "label price cap", "shipping cap"],
      valueSemantics: {
        unsetLabel: "Redo's default cap",
        summary: "Leave unset to use Redo's standard maximum label price.",
      },
    }),
    "returns/shipping/pickupMaxCoveredAmountEnabled": setting({
      label: "Cap covered pickup cost",
      description:
        "Limits how much of a carrier pickup fee Redo covers for the customer.",
      synonyms: ["pickup cost cap", "cover pickup"],
      valueSemantics: offOn,
    }),
    "returns/shipping/pickupMaxCoveredAmount": setting({
      label: "Maximum covered pickup amount",
      description: "The most Redo will cover toward a carrier pickup fee.",
      synonyms: ["pickup max", "pickup coverage amount"],
      dependencies: [
        {
          key: "returns/shipping/pickupMaxCoveredAmountEnabled",
          relationship: "requires",
          description: "Only used when the pickup cost cap is enabled.",
        },
      ],
    }),
    "returns/shipping/labelIncludeReturnType": setting({
      label: "Include return type on label",
      description:
        "Prints whether the request is a return, claim, or warranty on the label. On by default.",
      synonyms: ["return type on label", "label return type"],
      valueSemantics: onOff,
    }),
    "returns/shipping/labelCustomText": setting({
      label: "Custom label text",
      description:
        "The two custom print lines on return labels. Supports {{orderName}}, {{redoReturnId}}, {{loopId}}, and {{RMA}}.",
      synonyms: [
        "label text",
        "custom label text",
        "print lines on label",
        "label variables",
      ],
    }),
    "returns/shipping/packingInstructions": setting({
      label: "Custom packing instructions",
      description:
        "Instructions shown to the customer about how to pack the return.",
      synonyms: ["packing instructions", "how to pack"],
    }),
    "returns/shipping/manualLabelInstructions": setting({
      label: "Manual return label instructions",
      description:
        "Instructions customers see when their return requires a manual return label.",
      synonyms: ["manual label instructions", "own label instructions"],
    }),
    "returns/shipping/formLabelInstructions": setting({
      label: "QR code label instructions",
      description:
        "Instructions customers see when their return uses a QR code label. Use {{carrier}} to insert the carrier name.",
      synonyms: ["QR code instructions", "qr label instructions"],
    }),
    "returns/shipping/australiaPostPrinterlessEnabled": setting({
      label: "Australia Post printerless returns",
      description:
        "Tells customers with an Australia Post return label that they can show it at a participating Post Office without printing.",
      synonyms: ["auspost printerless", "printerless returns"],
      valueSemantics: offOn,
    }),
    "returns/shipping/australiaPostPrinterlessText": setting({
      label: "Printerless drop-off instructions",
      description:
        "The message shown on the returns portal and confirmation email when an Australia Post return label can be used without printing.",
      synonyms: ["auspost printerless message"],
      dependencies: [
        {
          key: "returns/shipping/australiaPostPrinterlessEnabled",
          relationship: "requires",
          description:
            "Only shown when Australia Post printerless returns is enabled.",
        },
      ],
    }),
    "returns/shipping/requireReturnShippingSignatureEnabled": setting({
      label: "Require signature on return shipments",
      description:
        "Requires a signature when the returned items are worth more than a threshold.",
      synonyms: [
        "signature required",
        "require signature",
        "signature on return",
      ],
      valueSemantics: offOn,
    }),
    "returns/shipping/requireReturnShippingSignatureThreshold": setting({
      label: "Signature threshold",
      description:
        "Return value above which a signature is required on the return shipment.",
      synonyms: ["signature threshold", "signature amount"],
      dependencies: [
        {
          key: "returns/shipping/requireReturnShippingSignatureEnabled",
          relationship: "requires",
          description: "Only used when signature on return is enabled.",
        },
      ],
    }),
    "returns/shipping/requireReturnShippingSignatureForTagsEnabled": setting({
      label: "Require signature for tagged products",
      description:
        "Requires a signature when the return contains products with specific tags.",
      synonyms: ["signature for tags", "tag signature requirement"],
      valueSemantics: offOn,
    }),
    "returns/shipping/requireReturnShippingSignatureForTagsTags": setting({
      label: "Signature-required product tags",
      description:
        "Product tags that force a signature requirement on the return shipment.",
      synonyms: ["signature tags", "which tags require signature"],
      dependencies: [
        {
          key: "returns/shipping/requireReturnShippingSignatureForTagsEnabled",
          relationship: "requires",
          description:
            "Only used when tag-based signature requirement is enabled.",
        },
      ],
    }),
    "returns/shipping/ignoreTeamCarrierOverrideInMerchantDash": setting({
      label: "Ignore carrier override in merchant dashboard",
      description:
        "Stops the team-level carrier override from applying to labels bought from the merchant dashboard.",
      synonyms: ["carrier override", "ignore carrier override"],
      valueSemantics: offOn,
    }),
  }),

  ...returnsGroup("returns.withdrawals_general", {
    "returns/withdrawals/enabled": setting({
      label: "Enable withdrawals",
      description:
        "Lets customers withdraw an order before it ships instead of returning it after delivery. On by default.",
      synonyms: [
        "enable withdrawals",
        "allow order cancellation",
        "withdraw order",
        "cancel an order after placing it",
        "cancel before it ships",
        "customer cancels their order",
      ],
      valueSemantics: onOff,
    }),
    "returns/withdrawals/shippingRefundMode": setting({
      label: "Shipping refund",
      description:
        "How the original shipping charge is refunded when an order is withdrawn. Withdrawals always refund shipping — the choice is whether that is the full amount or a set amount. Defaults to the full amount.",
      synonyms: ["shipping refund", "refund shipping on withdrawal"],
      valueSemantics: {
        defaultValue: "full",
        unsetLabel: "Refund full shipping amount",
        summary:
          "Not stored directly: the mode is derived from whether a fixed amount has been saved, so no stored value means the full shipping amount is refunded.",
        options: [
          { value: "full", label: "Refund full shipping amount" },
          { value: "fixed", label: "Refund a set amount" },
        ],
      },
    }),
    "returns/withdrawals/shippingRefundFixedAmount": setting({
      label: "Fixed shipping refund amount",
      description:
        "The flat shipping amount refunded on a withdrawal. Saving a value here is what switches the refund from the full shipping amount to a set amount.",
      synonyms: ["fixed shipping refund", "shipping refund amount"],
      dependencies: [
        {
          key: "returns/withdrawals/shippingRefundMode",
          relationship: "requires",
          description:
            "Only used when the shipping refund mode is a fixed amount.",
        },
      ],
    }),
    "returns/withdrawals/tagsToAddToAutoCancelledOrders": setting({
      label: "Tags to add to withdrawal orders",
      description:
        "Tags Redo adds to an order when it is withdrawn, for downstream reporting or automation.",
      synonyms: ["withdrawal tags", "tag cancelled orders"],
    }),
  }),

  ...returnsGroup("returns.withdrawals_notifications", {
    "returns/withdrawals/enableMerchantWithdrawalSubmittedEmail": setting({
      label: "Email merchant when a request is submitted",
      description:
        "Notifies merchant staff by email as soon as a customer submits a withdrawal request. On by default.",
      synonyms: ["notify merchant", "withdrawal submitted email"],
      valueSemantics: onOff,
    }),
    "returns/withdrawals/merchantEmailsForNotifications": setting({
      label: "Merchant notification recipients",
      description:
        "Email addresses that receive withdrawal request notifications.",
      synonyms: ["notification recipients", "who gets withdrawal emails"],
      dependencies: [
        {
          key: "returns/withdrawals/enableMerchantWithdrawalSubmittedEmail",
          relationship: "requires",
          description: "Only used when merchant notification email is enabled.",
        },
      ],
    }),
    "returns/withdrawals/enablethirdPartyFulfillmentCancellationEmail": setting(
      {
        label: "Email third-party fulfillment contacts",
        description:
          "Notifies the 3PL or fulfillment partner so they can stop the order from shipping.",
        synonyms: [
          "3PL email",
          "fulfillment partner notification",
          "warehouse email",
        ],
        valueSemantics: offOn,
      },
    ),
    "returns/withdrawals/thirdPartyFulfillmentCancellationEmailList": setting({
      label: "Third-party fulfillment recipients",
      description:
        "Email addresses at the fulfillment partner that receive withdrawal notifications.",
      synonyms: ["3PL recipients", "fulfillment contact emails"],
      dependencies: [
        {
          key: "returns/withdrawals/enablethirdPartyFulfillmentCancellationEmail",
          relationship: "requires",
          description:
            "Only used when third-party fulfillment email is enabled.",
        },
      ],
    }),
  }),

  ...returnsGroup("returns.withdrawals_auto_cancellation", {
    "returns/withdrawals/preventAutoCancellingUnfulfilledOrder": setting({
      label: "Prevent automatic order cancellation",
      description:
        "Restricts automatic cancellation of the unfulfilled order when a withdrawal is submitted. On its own it stops cancellation entirely; adding blocking tags or an order-age limit narrows it so only matching orders are spared and the rest still cancel automatically.",
      synonyms: ["prevent auto cancel", "manual cancellation", "do not cancel"],
      valueSemantics: {
        ...offOn,
        summary:
          "Off by default, so withdrawals cancel the unfulfilled order immediately.",
      },
    }),
    "returns/withdrawals/hourDelayForAutoCancellation": setting({
      label: "Auto-cancellation delay (hours)",
      description:
        "Order-age limit, not a waiting period: orders already older than this many hours are left for manual review instead of being cancelled automatically. Redo never waits — orders inside the window cancel right away.",
      synonyms: [
        "cancellation delay",
        "wait before cancelling",
        "order age limit",
      ],
      valueSemantics: {
        unit: "hours",
        unsetLabel: "No order-age limit",
        summary: "Leave blank to ignore order age.",
      },
      dependencies: [
        {
          key: "returns/withdrawals/preventAutoCancellingUnfulfilledOrder",
          relationship: "requires",
          description:
            "Only applies while automatic cancellation is being prevented.",
        },
      ],
    }),
    "returns/withdrawals/lateWithdrawalNoticeText": setting({
      label: "Late withdrawal notice",
      description:
        "Message shown to a customer whose withdrawal arrives too late to stop the shipment.",
      synonyms: ["late withdrawal message", "too late to cancel"],
      dependencies: [
        {
          key: "returns/withdrawals/preventAutoCancellingUnfulfilledOrder",
          relationship: "requires",
          description:
            "Only applies while automatic cancellation is being prevented.",
        },
      ],
    }),
    "returns/withdrawals/tagsToBlockAutoCancellation": setting({
      label: "Order tags that block auto-cancellation",
      description:
        "Orders carrying any of these tags are never cancelled automatically. Orders without them still cancel automatically.",
      synonyms: ["block cancellation", "do not cancel tags"],
      dependencies: [
        {
          key: "returns/withdrawals/preventAutoCancellingUnfulfilledOrder",
          relationship: "requires",
          description:
            "Only applies while automatic cancellation is being prevented.",
        },
      ],
    }),
  }),

  ...returnsGroup("returns.withdrawals_exclusions", {
    "returns/withdrawals/excludedCollections": setting({
      label: "Excluded collections",
      description: "Items in these collections cannot be withdrawn.",
      synonyms: [
        "withdrawal collection exclusions",
        "collections not eligible",
      ],
    }),
    "returns/withdrawals/excludedProductTags": setting({
      label: "Excluded product tags",
      description: "Items with these product tags cannot be withdrawn.",
      synonyms: ["withdrawal product tag exclusions", "tags not eligible"],
    }),
    "returns/withdrawals/excludedMetafields": setting({
      label: "Excluded product variant metafields",
      description:
        "Items whose variant metafields match these values cannot be withdrawn.",
      synonyms: ["exclude metafields", "variant metafield exclusions"],
    }),
  }),
  ...returnsGroup("returns.warehouse_grade_actions", {
    "returns/warehouse/gradeMappings": setting({
      label: "Grade actions",
      description:
        "For each grade the warehouse can assign, whether the item is accepted, flagged for review, or rejected, and whether accepting it also restocks it.",
      synonyms: [
        "grade actions",
        "accept grade",
        "reject grade",
        "flag grade for review",
        "restock by grade",
      ],
    }),
  }),
  ...returnsGroup("returns.automation_return_processing", {
    "returns/automation/returnProcessing/exchange": setting({
      label: "Process exchanges at status",
      description:
        "The return status at which an exchange is automatically processed.",
      synonyms: [
        "process exchange",
        "when to process exchange",
        "exchange auto process status",
      ],
    }),
    "returns/automation/returnProcessing/refund": setting({
      label: "Process refunds at status",
      description:
        "The return status at which a refund is automatically issued.",
      synonyms: [
        "process refund",
        "when to refund",
        "refund on delivery",
        "auto refund status",
      ],
      changeGuidance: {
        summary:
          "Refunding before Delivered pays the customer before the item is back.",
      },
    }),
    "returns/automation/returnProcessing/storeCredit": setting({
      label: "Process store credit at status",
      description:
        "The return status at which store credit is automatically issued.",
      synonyms: [
        "process store credit",
        "when to issue credit",
        "store credit status",
      ],
    }),
    "returns/automation/returnProcessing/exchangeDelay": setting({
      label: "Exchange processing delay",
      description:
        "Days Redo waits after the exchange status is reached before processing.",
      synonyms: ["exchange delay", "wait before exchange", "delay exchange"],
    }),
    "returns/automation/returnProcessing/refundDelay": setting({
      label: "Refund processing delay",
      description:
        "Days Redo waits after the refund status is reached before processing.",
      synonyms: ["refund delay", "wait before refund", "delay refund"],
    }),
    "returns/automation/returnProcessing/storeCreditDelay": setting({
      label: "Store credit processing delay",
      description:
        "Days Redo waits after the store credit status is reached before processing.",
      synonyms: ["store credit delay", "delay store credit"],
    }),
    "returns/automation/returnProcessing/reviewAfterWarehouseProcessing":
      setting({
        label: "Require review after warehouse processing",
        description:
          "When the warehouse processes a return, place it in Needs review instead of auto-completing so a merchant must approve before refunds, store credit, or exchanges are issued.",
        synonyms: [
          "review after warehouse",
          "hold returns after warehouse",
          "needs review after WMS",
          "manual approval after warehouse",
          "warehouse processed review",
        ],
        changeGuidance: {
          summary:
            "Turning this on stops auto-refund from firing when the warehouse processes a return; a merchant must approve each return.",
        },
      }),
    "returns/automation/returnProcessing/requireImageForUndeclaredItems":
      setting({
        label: "Require image for undeclared items",
        description:
          "Requires warehouse operators to capture at least one image when recording an undeclared item during return processing.",
        synonyms: [
          "require image for undeclared items",
          "undeclared item photo",
          "photo required for extra item",
        ],
      }),
  }),
  ...returnsGroup("returns.automation_claim_processing", {
    "returns/automation/claimProcessing/exchange": setting({
      label: "Process claim exchanges at status",
      description:
        "The claim status at which an exchange is automatically processed.",
      synonyms: ["claim exchange processing", "when to process claim exchange"],
    }),
    "returns/automation/claimProcessing/refund": setting({
      label: "Process claim refunds at status",
      description:
        "The claim status at which a refund is automatically issued.",
      synonyms: ["claim refund processing", "when to refund a claim"],
    }),
    "returns/automation/claimProcessing/storeCredit": setting({
      label: "Process claim store credit at status",
      description:
        "The claim status at which store credit is automatically issued.",
      synonyms: ["claim store credit", "when to issue claim credit"],
    }),
    "returns/automation/claimProcessing/exchangeDelay": setting({
      label: "Claim exchange processing delay",
      description:
        "Days Redo waits after the claim exchange status is reached before processing.",
      synonyms: ["claim exchange delay"],
    }),
    "returns/automation/claimProcessing/refundDelay": setting({
      label: "Claim refund processing delay",
      description:
        "Days Redo waits after the claim refund status is reached before processing.",
      synonyms: ["claim refund delay"],
    }),
    "returns/automation/claimProcessing/storeCreditDelay": setting({
      label: "Claim store credit processing delay",
      description:
        "Days Redo waits after the claim store credit status is reached before processing.",
      synonyms: ["claim store credit delay"],
    }),
  }),
  ...returnsGroup("returns.automation_inventory", {
    "returns/automation/inventory/restock": setting({
      label: "Restock returned items",
      description:
        "Which returned items are automatically added back to inventory when a return is processed.",
      synonyms: [
        "restock",
        "restock returns",
        "automatic restocking",
        "restock damaged",
        "add back to inventory",
      ],
      changeGuidance: {
        summary:
          "Restocking damaged items puts unsellable stock back on the storefront.",
      },
    }),
  }),
  ...returnsGroup("returns.automation_label_expiration", {
    "labelExpiration/days": setting({
      label: "Label expiration days",
      description:
        "How many days a return shipping label stays valid before it expires.",
      synonyms: [
        "label expiration days",
        "how long is the label valid",
        "return label expires",
      ],
    }),
    "labelExpiration/strategy": setting({
      label: "Label expiration starts from",
      description:
        "Whether the expiration window is counted from the return date or the original order date.",
      synonyms: [
        "label expiration strategy",
        "expiration counted from",
        "expire from order date",
      ],
    }),
    "labelExpiration/reminderEmails": setting({
      label: "Send label reminder emails",
      description:
        "Emails customers before their return label expires, reminding them to ship.",
      synonyms: [
        "label reminder emails",
        "remind customer to ship",
        "return reminder",
      ],
      valueSemantics: offOn,
    }),
    "labelExpiration/reminderEmailDays": setting({
      label: "Reminder email days",
      description: "Days before expiration on which reminder emails are sent.",
      synonyms: ["reminder days", "when to send reminders"],
      dependencies: [
        {
          key: "labelExpiration/reminderEmails",
          relationship: "controlledBy",
          description: "Only used when reminder emails are enabled.",
        },
      ],
    }),
    "labelExpiration/compensationMethodsToRemind": setting({
      label: "Remind for compensation methods",
      description: "Which return compensation methods trigger reminder emails.",
      synonyms: [
        "which returns get reminders",
        "reminder compensation methods",
      ],
      dependencies: [
        {
          key: "labelExpiration/reminderEmails",
          relationship: "controlledBy",
          description: "Only used when reminder emails are enabled.",
        },
      ],
    }),
    "labelExpiration/expirationEmails": setting({
      label: "Send label expiration emails",
      description: "Emails customers once their return label has expired.",
      synonyms: ["expiration emails", "expired label email"],
      valueSemantics: offOn,
    }),
    "labelExpiration/shouldReopenReturnWhenLabelUsed": setting({
      label: "Reopen return when expired label is used",
      description:
        "Reopens a closed return if the customer ships on an already-expired label.",
      synonyms: [
        "reopen return",
        "expired label used",
        "customer shipped late",
      ],
      valueSemantics: offOn,
    }),
    "labelExpiration/reopenReturnAfterDays": setting({
      label: "Reopen return after days",
      description:
        "How long Redo waits after an expired label is used before reopening the return.",
      synonyms: ["reopen delay", "reopen after days"],
      dependencies: [
        {
          key: "labelExpiration/shouldReopenReturnWhenLabelUsed",
          relationship: "controlledBy",
          description:
            "Only used when reopening on expired-label use is enabled.",
        },
      ],
    }),
    "labelExpiration/flagReturnWhenReopened": setting({
      label: "Flag reopened returns",
      description:
        "Flags a return for merchant review when it is reopened from an expired label.",
      synonyms: ["flag reopened return", "review reopened returns"],
      valueSemantics: onOff,
    }),
  }),
  ...returnsGroup("returns.automation_agent_review", {
    "returns/automation/agentReviewRiskConfigs": setting({
      label: "Agent review actions by risk level",
      description:
        "What Redo does with a return at each fraud risk level — take no action, flag it for review, or hold it.",
      synonyms: [
        "agent review",
        "risk level actions",
        "flag risky returns",
        "hold high risk returns",
        "fraud review",
      ],
      changeGuidance: {
        summary:
          "Setting high risk to no action lets suspected-fraud returns process automatically.",
      },
    }),
  }),
  ...returnsGroup("returns.automation_merchant_created", {
    "returns/automation/merchantCreatedReturns/enableMerchantReturnFlow":
      setting({
        label: "Enable merchant-created returns",
        description:
          "Lets merchant staff create a return on a customer's behalf from the Redo admin.",
        synonyms: [
          "merchant created returns",
          "create return for customer",
          "staff create return",
        ],
        valueSemantics: offOn,
      }),
    "returns/automation/merchantCreatedReturns/merchantCreatedReturnLabelChargeDefault":
      setting({
        label: "Charge customer for return label by default",
        description:
          "Sets the initial state of the customer return-label charge for merchant-created returns and claims when that choice is available. Auto follows the applicable return flow's Safety net setting and existing label coverage rules.",
        synonyms: [
          "merchant return label payer default",
          "charge customer return shipping by default",
          "default customer paid label",
          "merchant created label charge",
        ],
        risk: automationMoneyRisk,
        valueSemantics: {
          defaultValue: "auto",
          selectionMode: "single",
          options: [
            {
              value: "auto",
              label: "Auto",
              description:
                "Follow the return flow's Safety net setting and existing label coverage rules.",
            },
            {
              value: "on",
              label: "On",
              description:
                "Initially charge the customer when the label cost can be deducted.",
            },
            {
              value: "off",
              label: "Off",
              description: "Initially have the merchant pay for the label.",
            },
          ],
        },
        changeGuidance: {
          summary:
            "On can deduct return-label costs from a customer's refund or store credit; Off makes the merchant responsible by default.",
        },
      }),
    "returns/automation/merchantCreatedReturns/merchantCreatedClaimsRequireShipping":
      setting({
        label: "Merchant-created claims require shipping",
        description:
          "Requires a shipping label on claims that merchant staff create.",
        synonyms: [
          "claims require shipping",
          "merchant claim shipping",
          "require label on claims",
        ],
        valueSemantics: offOn,
      }),
  }),
  ...returnsGroup("returns.automation_green_returns", {
    "returns/automation/autoProcessGreenReturns": setting({
      label: "Auto-process green returns",
      description:
        "Automatically processes green (keep-the-item) returns without waiting for a shipment.",
      synonyms: [
        "green returns",
        "keep the item",
        "auto process green returns",
        "no return necessary",
      ],
      valueSemantics: onOff,
    }),
    "returns/automation/autoProcessGreenReturnTypes": setting({
      label: "Green return types to auto-process",
      description:
        "Which request types — returns, claims, or warranties — are auto-processed when green.",
      synonyms: ["green return types", "which green returns auto process"],
      dependencies: [
        {
          key: "returns/automation/autoProcessGreenReturns",
          relationship: "controlledBy",
          description: "Only used when green auto-processing is enabled.",
        },
      ],
    }),
    "returns/automation/autoProcessMerchantCreatedGreenReturnTypes": setting({
      label: "Merchant-created green return types to auto-process",
      description:
        "Which merchant-created request types are auto-processed when green.",
      synonyms: [
        "merchant created green returns",
        "staff created green returns",
      ],
    }),
  }),
  ...returnsGroup("returns.automation_dropoff_processing", {
    "returns/automation/dropoffAutoProcessCompensationMethods": setting({
      label: "Dropoff auto-process compensation methods",
      description:
        "Which compensation methods are automatically processed, per dropoff provider, once dropoff verification passes.",
      synonyms: [
        "dropoff auto process",
        "auto process dropoff",
        "dropoff compensation methods",
        "auto process compensation methods",
      ],
      dependencies: [
        {
          key: "returns/shipping/dropoffs",
          relationship: "requires",
          description:
            "Only applies to dropoff providers that are enabled on the Shipping settings page.",
        },
      ],
    }),
  }),
  ...returnsGroup("returns.automation_general", {
    "returns/automation/waiveRefundStoreCreditBelowAmount": setting({
      label: "Waive refunds below amount",
      description:
        "Store credit or refund amounts below this value are waived instead of paid out.",
      synonyms: [
        "waive refund",
        "minimum refund amount",
        "skip small refunds",
        "refund threshold",
      ],
      risk: {
        level: "high",
        reason: "Directly controls whether customers are paid out at all.",
      },
    }),
    "returns/automation/addReturnTagToOriginalOrder": setting({
      label: "Add return tag to original order",
      description:
        "Tags the original order when a return is created against it.",
      synonyms: ["return tag", "tag original order", "order tag on return"],
      valueSemantics: offOn,
    }),
    "returns/automation/defaultReimburseOriginalShipping": setting({
      label: "Reimburse original shipping by default",
      description:
        "Includes the original shipping cost in the refund by default.",
      synonyms: [
        "reimburse shipping",
        "refund shipping cost",
        "original shipping refund",
      ],
      valueSemantics: offOn,
    }),
  }),
  ...returnsGroup(RETURNS_AUTOMATION_CODE_FUNCTIONS_GROUP_ID, {
    [EXCHANGE_ORDER_CREATION_ARTIFACT_SETTING_ID]: setting({
      label: "Exchange order function",
      owner: exchangeSettingsOwner,
      description:
        "Runs custom code when Redo creates a Shopify exchange order or draft order to add tags, append the order note, add note attributes, or set line item properties.",
      synonyms: [
        "exchange order code",
        "custom exchange tags",
        "custom exchange note",
        "redojs exchange function",
        "line item properties",
      ],
      changeGuidance: {
        summary:
          "Custom code changes metadata on every future Shopify exchange order. Review and test the function before saving it.",
      },
    }),
    [RETURN_ADJUSTMENT_ARTIFACT_SETTING_ID]: setting({
      label: "Return adjustment function",
      description:
        "Runs custom code that computes the bonus or fee for return options that opt into code-driven adjustments, replacing the manually configured amount.",
      synonyms: [
        "return adjustment function",
        "code driven return adjustment",
        "custom return bonus or fee code",
        "override adjustment with code",
      ],
      risk: {
        level: "high",
        reason:
          "Custom code that changes what a customer is paid on every return step that opts in.",
      },
      changeGuidance: {
        summary:
          "Changing this function changes payout amounts on every future return whose flow opts into code-driven adjustments. Test it before saving.",
      },
    }),
  }),
  ...returnsGroup("returns.automation_shopify", {
    "returns/automation/shopify/createShopifyReturns": setting({
      label: "Create Shopify returns",
      description:
        "Mirrors each Redo return into Shopify as a native Shopify return.",
      synonyms: [
        "create shopify returns",
        "shopify native returns",
        "sync returns to shopify",
      ],
      valueSemantics: offOn,
    }),
    "returns/automation/shopify/createShopifyReturnsForClaimsAndWarranties":
      setting({
        label: "Create Shopify returns for claims and warranties",
        description:
          "Also mirrors claims and warranties into Shopify, not just returns.",
        synonyms: ["shopify claims", "shopify warranties"],
        valueSemantics: offOn,
      }),
    "returns/automation/shopify/createShopifyReturnTypes": setting({
      label: "Shopify return compensation types",
      description:
        "Which compensation methods create a corresponding Shopify return.",
      synonyms: ["shopify return types", "which returns go to shopify"],
    }),
    "returns/automation/shopify/archiveShopifyOrdersAfterReturn": setting({
      label: "Archive Shopify orders after return",
      description: "Archives the Shopify order once its return is processed.",
      synonyms: ["archive shopify order", "close shopify order"],
      valueSemantics: onOff,
    }),
    "returns/automation/shopify/closeShopifyReturnWhenProcessed": setting({
      label: "Close Shopify return when processed",
      description:
        "Whether Redo closes the Shopify return when the Redo return is processed, and under what condition.",
      synonyms: ["close shopify return", "shopify return closing"],
    }),
    "returns/automation/shopify/disableRefundLineItems": setting({
      label: "Disable refund line items",
      description:
        "Stops Redo from itemizing refunded products on the Shopify refund.",
      synonyms: ["refund line items", "itemized refund"],
      valueSemantics: offOn,
    }),
    "returns/automation/shopify/shouldAutoProcessRedoReturnWhenShopifyReturnCloses":
      setting({
        label: "Auto-process Redo return when Shopify return closes",
        description:
          "Processes the Redo return automatically when the matching Shopify return is closed in Shopify. Refunding a return from the Shopify admin closes it, so with this on Redo issues a second refund on top of the merchant's and the customer is paid twice.",
        synonyms: [
          "shopify closes return",
          "process from shopify",
          "shopify return closed",
          "duplicate refund",
          "refunded twice",
        ],
        valueSemantics: offOn,
        risk: {
          level: "high",
          reason:
            "Lets a Shopify-side action release refunds without anyone processing the return in Redo.",
        },
        changeGuidance: {
          summary:
            "Only turn this on if Redo issues all of your return refunds. If your team refunds returns from the Shopify admin, that refund closes the Shopify return and Redo refunds the same items again.",
        },
      }),
    "returns/automation/shopify/storeCreditExpirationEnabled": setting({
      label: "Expire Shopify store credit",
      description: "Gives store credit issued in Shopify an expiration date.",
      synonyms: [
        "store credit expiration",
        "credit expires",
        "expiring store credit",
      ],
      valueSemantics: offOn,
    }),
    "returns/automation/shopify/storeCreditExpirationDays": setting({
      label: "Store credit expiration days",
      description: "How many days issued store credit stays valid.",
      synonyms: ["credit expiration days", "how long credit lasts"],
      dependencies: [
        {
          key: "returns/automation/shopify/storeCreditExpirationEnabled",
          relationship: "controlledBy",
          description: "Only used when store credit expiration is enabled.",
        },
      ],
    }),
    "returns/automation/shopify/giftCardRecipient": setting({
      label: "Gift card recipient",
      description:
        "Who receives a gift card issued for a gift return — the recipient or the original purchaser.",
      synonyms: [
        "gift card recipient",
        "who gets the gift card",
        "gift return credit",
      ],
    }),
    "returns/automation/shopify/disposeShopifyReverseFulfillmentOnRestock":
      setting({
        label: "Dispose Shopify reverse fulfillment on restock",
        description:
          "Marks the Shopify reverse fulfillment as disposed when Redo restocks the item.",
        synonyms: ["reverse fulfillment", "dispose fulfillment"],
        valueSemantics: offOn,
      }),
    "returns/automation/shopify/restockShopifyRefunds": setting({
      label: "Restock through Shopify refunds",
      description:
        "Lets Shopify handle restocking as part of the refund rather than Redo restocking directly.",
      synonyms: ["shopify restock", "restock via refund"],
      valueSemantics: offOn,
    }),
    "returns/automation/shopify/skipEcommerceProviderRestock": setting({
      label: "Skip ecommerce provider restock",
      description:
        "Stops Redo from restocking in the ecommerce platform at all.",
      synonyms: ["skip restock", "no platform restock"],
      valueSemantics: offOn,
    }),
    "returns/automation/shopify/applyRestockingFeeToShopifyReturns": setting({
      label: "Apply restocking fee to Shopify returns",
      description:
        "Passes the Redo restocking fee through to the Shopify return.",
      synonyms: ["restocking fee", "shopify restocking fee"],
      valueSemantics: offOn,
    }),
    "returns/automation/shopify/syncReturnFeesToShopify": setting({
      label: "Sync return fees to Shopify",
      description:
        "Writes Redo return fees onto the Shopify return so they appear in Shopify reporting.",
      synonyms: ["sync fees", "return fees in shopify"],
      valueSemantics: offOn,
    }),
    "returns/automation/shopify/refundAllReturnedItems": setting({
      label: "Always refund all returned items",
      description:
        "Refunds every returned item rather than only the ones Redo determines are refundable.",
      synonyms: ["refund all items", "always create refund"],
      valueSemantics: offOn,
    }),
    "returns/automation/shopify/refundDutiesPaid": setting({
      label: "Refund duties paid",
      description: "Includes duties paid on the original order in the refund.",
      synonyms: ["refund duties", "duties refund", "customs refund"],
      valueSemantics: offOn,
    }),
    "returns/automation/shopify/dutyDrawbackEnabled": setting({
      label: "Enable duty drawback",
      description: "Claims duty drawback on eligible international returns.",
      synonyms: ["duty drawback", "reclaim duties"],
      valueSemantics: offOn,
    }),
    "returns/automation/shopify/dutyDrawbackEligibleCountries": setting({
      label: "Duty drawback eligible countries",
      description: "Countries where duty drawback is claimed.",
      synonyms: ["drawback countries", "duty drawback countries"],
      dependencies: [
        {
          key: "returns/automation/shopify/dutyDrawbackEnabled",
          relationship: "controlledBy",
          description: "Only used when duty drawback is enabled.",
        },
      ],
    }),
    "returns/automation/shopify/skipExchangeRefunds": setting({
      label: "Skip exchange refunds",
      description:
        "Stops Redo from creating a Shopify refund for the returned side of an exchange.",
      synonyms: ["skip exchange refund", "no refund on exchange"],
      valueSemantics: offOn,
    }),
    "returns/automation/shopify/showStoreCreditOnRefundNote": setting({
      label: "Show store credit on refund note",
      description:
        "Records the store credit amount in the Shopify refund note.",
      synonyms: ["refund note", "store credit note"],
      valueSemantics: offOn,
    }),
    "returns/automation/shopify/createSeparateShopifyReturnForGreenReturns":
      setting({
        label: "Separate Shopify return for green returns",
        description:
          "Creates a distinct Shopify return for green (keep-the-item) returns.",
        synonyms: ["green return shopify", "separate green return"],
        valueSemantics: offOn,
      }),
    "returns/automation/shopify/createMultipleShopifyReturns": setting({
      label: "Create multiple Shopify returns",
      description:
        "Allows more than one Shopify return per order instead of merging into one.",
      synonyms: ["multiple shopify returns", "split shopify returns"],
      valueSemantics: offOn,
    }),
    "returns/automation/shopify/addTrackingToShopifyReturns": setting({
      label: "Add tracking to Shopify returns",
      description:
        "Writes the return shipment tracking number onto the Shopify return.",
      synonyms: ["tracking on shopify return", "return tracking number"],
      valueSemantics: onOff,
    }),
    "returns/automation/shopify/addOrderNotes": setting({
      label: "Add order notes in Shopify",
      description:
        "Writes a note onto the Shopify order describing the return.",
      synonyms: ["shopify order notes", "note on order"],
      valueSemantics: offOn,
    }),
  }),

  ...returnsGroup("returns.managed_returns_general", {
    "redoManagedReturns/gradeMappings": setting({
      label: "Grade actions",
      description:
        "For each condition grade, whether the warehouse accepts the returned item and whether it is restocked when that grade is assigned during processing.",
      synonyms: [
        "grade actions",
        "grade mappings",
        "grade to outcome",
        "acceptance by grade",
        "restock by grade",
      ],
    }),
  }),

  ...returnsGroup("returns.leftover_exchange_value", {
    "exchanges/excessExchangeValue": setting({
      label: "Exchange value price difference & out-of-stock policy",
      description:
        "Chooses how customers receive leftover exchange value and handles unavailable replacement items.",
      synonyms: ["leftover exchange value", "exchange refund choice"],
    }),
    "exchanges/leftOverValueShopperSelectionIncentive/incentivizedMethod":
      setting({
        label: "Apply incentive as",
        description: "Chooses a store-credit bonus or refund fee.",
        synonyms: ["store credit bonus", "refund fee"],
      }),
    "exchanges/leftOverValueShopperSelectionIncentive/value": setting({
      label: "Bonus or fee amount",
      description: "Sets the incentive amount for the shopper's selection.",
      synonyms: ["bonus amount", "fee amount"],
    }),
    "exchanges/leftOverValueShopperSelectionIncentive/perCurrencyAmountsEnabled":
      setting({
        label: "Per-currency incentive amounts",
        description:
          "Uses explicit incentive amounts for individual currencies.",
        synonyms: ["currency-specific incentive"],
      }),
    "exchanges/leftOverValueShopperSelectionIncentive/currencyOverrides":
      setting({
        label: "Currency-specific incentive amounts",
        description: "Sets incentive amounts by presentment currency.",
        synonyms: ["incentive currency overrides"],
      }),
  }),
};

export type ReturnsFlatTeamSettingKey =
  keyof typeof ReturnsFlatSettingCatalogRegistry;

const ReturnsFlatTeamSettingKeys = Object.keys(
  ReturnsFlatSettingCatalogRegistry,
) as ReturnsFlatTeamSettingKey[];

const ReturnsFlatTeamSettingKeySet = new Set<string>(
  ReturnsFlatTeamSettingKeys,
);

export function isReturnsFlatTeamSettingKey(
  key: string,
): key is ReturnsFlatTeamSettingKey {
  return ReturnsFlatTeamSettingKeySet.has(key);
}

export const ReturnsFlatTeamSettingCatalogDefinitions =
  ReturnsFlatTeamSettingKeys.map((key) => {
    const definition = ReturnsFlatSettingCatalogRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: ReturnsFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    ReturnsFlatTeamSettingKey,
    ReturnsFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<ReturnsFlatTeamSettingKey>
  >[];

export const ReturnsFlatTeamSettingMetadataByKey = Object.fromEntries(
  ReturnsFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<ReturnsFlatTeamSettingKey, SettingMetadata>;
